import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '../ui/button';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public override state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
    }

    private handleReset = () => {
        this.setState({ hasError: false, error: null });
    };

    public override render() {
        if (this.state.hasError) {
            return (
                <div className="flex min-h-screen items-center justify-center bg-background p-4">
                    <div className="w-full max-w-md space-y-4 rounded-lg border bg-card p-6 text-center shadow-lg">
                        <h2 className="text-2xl font-bold text-destructive">Something went wrong</h2>
                        <p className="text-sm text-muted-foreground">
                            An unexpected error occurred. Please try refreshing the page or contact support if the
                            problem persists.
                        </p>
                        {this.state.error && (
                            <details className="rounded-md bg-muted p-3 text-left text-xs">
                                <summary className="cursor-pointer font-medium">Error details</summary>
                                <pre className="mt-2 overflow-auto">{this.state.error.message}</pre>
                            </details>
                        )}
                        <div className="flex gap-2">
                            <Button onClick={this.handleReset} variant="outline" className="flex-1">
                                Try Again
                            </Button>
                            <Button
                                onClick={() => {
                                    window.location.href = '/';
                                }}
                                className="flex-1"
                            >
                                Go Home
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
